import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { PreviewCanvas, PreviewCanvasRef } from "@/components/PreviewCanvas";
import { useRef } from "react";
import type { Background } from "@/data/backgrounds";
import type { Template } from "@/data/templates";

interface CompositionControlsProps {
  letters: { [key: string]: string };
  text: string;
  setText: (text: string) => void;
  scale: number;
  setScale: (scale: number) => void;
  isManualScale: boolean;
  aspectRatio: string;
  setAspectRatio: (ratio: string) => void;
  templates: Template[];
  selectedTemplate: Template;
  setSelectedTemplate: (template: Template) => void;
  backgrounds: Background[];
  selectedBackground: Background;
}

const aspectRatios = [
  { id: "square", label: "1:1", ratio: "1:1" },
  { id: "landscape", label: "4:3", ratio: "4:3" },
  { id: "portrait", label: "3:4", ratio: "3:4" },
  { id: "wide", label: "16:9", ratio: "16:9" },
];

// Security: Maximum text length to prevent DoS and memory issues
const MAX_TEXT_LENGTH = 5000;

export const CompositionControls = ({
  letters,
  text,
  setText,
  scale,
  setScale,
  isManualScale,
  aspectRatio,
  setAspectRatio,
  templates,
  selectedTemplate,
  setSelectedTemplate,
  backgrounds,
  selectedBackground,
}: CompositionControlsProps) => {
  const canvasRef = useRef<PreviewCanvasRef>(null);

  const handleDownload = () => {
    toast.success("Downloading high-resolution image...");
    canvasRef.current?.downloadHighRes(3);
  };

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      {/* Preview Canvas - Centered */}
      <div className="w-full max-w-2xl">
        <Card className="p-2 md:p-4 bg-card card-float neon-border tilt-on-hover transition-all duration-300 h-fit">
          {/* Preview Canvas - Inside card border */}
          <div className="fade-up rounded-[var(--radius)] overflow-hidden">
            <PreviewCanvas
              ref={canvasRef}
              letters={letters}
              text={text}
              scale={scale}
              isManualScale={isManualScale}
              aspectRatio={aspectRatio}
              background={selectedBackground}
              shadowEnabled={true}
            />
          </div>

          {/* Aspect Ratio Selection - Inside card, below canvas (Underline Tabs) */}
          <div className="flex justify-center gap-4 mt-2 pt-2 fade-up">
            {aspectRatios.map((ratio) => (
              <button
                key={ratio.id}
                onClick={() => setAspectRatio(ratio.id)}
                className={`text-xs transition-all duration-200 pb-1 font-medium cursor-pointer ${
                  aspectRatio === ratio.id
                    ? 'text-foreground border-b-2 border-foreground'
                    : 'text-muted-foreground border-b-2 border-transparent hover:text-foreground'
                }`}
                title={ratio.label}
              >
                {ratio.label}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Controls - Centered Below Canvas */}
      <div className="w-full max-w-2xl">
        <Card className="p-2 md:p-4 bg-card card-float neon-border tilt-on-hover transition-all duration-300">
          {/* Message Input - TOP (MOST IMPORTANT) */}
          <div className="mb-2 md:mb-3 fade-up sticky top-0 bg-card z-10 -mx-2 md:-mx-4 px-2 md:px-4 py-1">
            <Textarea
              value={text}
              onChange={(e) => {
                // Security: Prevent excessive input that could cause DoS
                if (e.target.value.length <= MAX_TEXT_LENGTH) {
                  setText(e.target.value);
                } else {
                  toast.error(`Maximum ${MAX_TEXT_LENGTH} characters allowed`);
                }
              }}
              maxLength={MAX_TEXT_LENGTH}
              placeholder="Type your message..."
              className="h-8 md:h-9 resize-none transition-all duration-300 rounded-[var(--radius)] px-1.5 md:px-2 py-0.5 md:py-1"
            />
          </div>

          {/* Scale Slider - Moved below text input */}
          <div className="mb-2 md:mb-3 fade-up">
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs md:text-sm font-medium text-foreground">
                Scale
              </label>
              <span className="text-xs font-medium text-muted-foreground transition-colors duration-300">{scale}%</span>
            </div>
            <Slider
              value={[scale]}
              onValueChange={(value) => setScale(value[0])}
              min={50}
              max={200}
              step={10}
              className="mb-1"
            />
            <div className="flex justify-between text-xs text-muted-foreground transition-colors duration-300">
              <span>Small</span>
              <span>Large</span>
            </div>
          </div>

          {/* Template Selector - Gradient Buttons */}
          <div className="mb-2 md:mb-3">
            <div className="grid grid-cols-8 gap-1 max-h-40 overflow-y-auto p-1 -m-1">
              {templates.map((template) => {
                // Find the paired background for this template
                const pairedBg = backgrounds.find(bg => bg.id === template.defaultBackgroundId);
                // Generate gradient CSS for the button
                const gradientStyle = pairedBg ? {
                  background: pairedBg.type === 'solid'
                    ? pairedBg.colors[0]
                    : pairedBg.type === 'linear'
                    ? `linear-gradient(${pairedBg.angle || 0}deg, ${pairedBg.colors.join(', ')})`
                    : `radial-gradient(circle, ${pairedBg.colors.join(', ')})`
                } : {};

                return (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`aspect-square transition-all duration-200 rounded-[var(--radius)] ${
                      selectedTemplate.id === template.id
                        ? 'ring-2 ring-offset-1 ring-foreground'
                        : ''
                    }`}
                    title={template.name}
                    style={gradientStyle}
                  />
                );
              })}
            </div>
          </div>

          {/* Download Button */}
          <Button
            onClick={handleDownload}
            variant="default"
            className="w-full text-xs md:text-sm transition-all duration-200 hover:scale-105 rounded-[var(--radius)] shadow-[var(--shadow)] hover:shadow-[var(--shadow-lg)]"
          >
            <Download className="mr-1 h-3 w-3 md:h-4 md:w-4" />
            Download
          </Button>
        </Card>
      </div>
    </div>
  );
};
